// Generated by OrchestKit Claude Plugin
// Created: 2026-09-12

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { appeler } from '../../api/client.ts';
import { Carte, Erreur } from '../../composants/communs.tsx';
import type { Fournisseur } from './types.ts';

/**
 * Création ET modification d'un fournisseur — amende Q-021.
 *
 * UN seul composant pour les deux gestes, comme pour les clients : les
 * champs et les règles sont identiques, et deux formulaires jumeaux
 * divergeraient au premier changement.
 *
 * Aucune boutique n'est demandée : la fiche est globale.
 */
export function FormulaireFournisseur({
  fournisseur,
  onFerme,
}: {
  /** Absent : création. Présent : modification de cette fiche. */
  fournisseur?: Fournisseur;
  onFerme: () => void;
}): ReactNode {
  const cache = useQueryClient();
  const modification = fournisseur !== undefined;

  const [nom, setNom] = useState(fournisseur?.nom ?? '');
  const [telephone, setTelephone] = useState(fournisseur?.telephone ?? '');
  const [adresse, setAdresse] = useState(fournisseur?.adresse ?? '');
  const [repere, setRepere] = useState(fournisseur?.repere ?? '');

  const complet = nom.trim().length >= 2;

  const envoi = useMutation({
    mutationFn: () => {
      const corps = {
        nom: nom.trim(),
        telephone: telephone.trim() === '' ? null : telephone.trim(),
        adresse: adresse.trim() === '' ? null : adresse.trim(),
        repere: repere.trim() === '' ? null : repere.trim(),
      };

      return modification
        ? appeler<{ id: string }>(`/fournisseurs/${fournisseur.id}`, {
            methode: 'PATCH',
            corps,
          })
        : appeler<{ id: string }>('/fournisseurs', { methode: 'POST', corps });
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['fournisseurs'] });
      onFerme();
    },
  });

  return (
    <Carte
      titre={modification ? `Modifier ${fournisseur.nom}` : 'Nouveau fournisseur'}
      actions={
        <button type="button" className="bouton--discret" onClick={onFerme}>
          Annuler
        </button>
      }
    >
      <div className="champs">
        <div className="champ">
          <label htmlFor="fournisseur-nom">Nom</label>
          <input
            id="fournisseur-nom"
            maxLength={100}
            value={nom}
            onChange={(evenement) => {
              setNom(evenement.target.value);
            }}
          />
          <small>
            Il doit être unique : c’est ce qui empêche le même grossiste
            d’apparaître sous trois orthographes.
          </small>
        </div>

        <div className="champ">
          <label htmlFor="fournisseur-telephone">Téléphone (facultatif)</label>
          <input
            id="fournisseur-telephone"
            inputMode="tel"
            maxLength={20}
            value={telephone}
            onChange={(evenement) => {
              setTelephone(evenement.target.value);
            }}
          />
        </div>

        <div className="champ">
          <label htmlFor="fournisseur-adresse">Adresse (facultatif)</label>
          <input
            id="fournisseur-adresse"
            maxLength={255}
            value={adresse}
            onChange={(evenement) => {
              setAdresse(evenement.target.value);
            }}
          />
        </div>

        <div className="champ">
          <label htmlFor="fournisseur-repere">Repère (facultatif)</label>
          <input
            id="fournisseur-repere"
            maxLength={255}
            value={repere}
            onChange={(evenement) => {
              setRepere(evenement.target.value);
            }}
          />
          <small>« En face du marché Dantokpa », quand l’adresse ne suffit pas.</small>
        </div>
      </div>

      <Erreur erreur={envoi.error} />
      <div className="ligne" style={{ marginTop: 12 }}>
        <button
          type="button"
          disabled={!complet || envoi.isPending}
          onClick={() => {
            envoi.mutate();
          }}
        >
          {envoi.isPending
            ? 'Enregistrement…'
            : modification
              ? 'Enregistrer les modifications'
              : 'Créer le fournisseur'}
        </button>
      </div>
    </Carte>
  );
}
